/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, Icon, ProfileSettings } from '@components';
import WalkThroughModal from '@components/user-hints/WalkThroughModal';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus, WalkthroughItem } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { EVENT_MANAGER_ROLES, LEAD_MANAGEMENT_ROLES } from '@constants/role';
import { AUTH, BRANCHES, GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { PROFILE_ITEM_PATHS } from '@constants/sideMenu';
import { STORAGE_KEYS } from '@constants/storage';
import testId from '@constants/testId';
import {
  useLazyAppConfigQuery,
  useUpdateWelcomeTourMutation
} from '@features/authentication/queries/AuthQuery';
import {
  useLazyGetAllBranchesQuery,
  useLazyGetBranchByIdQuery,
  useLazyGetBranchConfigQuery
} from '@features/branch-management/queries';
import { setMemberCustomFields } from '@features/branch-management/slices/branchConfigSlice';
import {
  useLazyGetOrganizationConfigQuery,
  useLazyGetOrgFeatureFlagsConfigQuery
} from '@features/group-management/queries/GroupQuery';
import {
  enterTrialMode,
  exitTrialMode
} from '@features/trial-mode/slices/trialSlice';
import useNotify from '@hooks/notify';
import AccountModals from '@layouts/side-nav-bar/AccountModals';
import { ApiError } from '@types';
import useOnClickOutside from '@utils/hooks';
import { getLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

import {
  setAccountType,
  setActiveTourHint,
  setAppConfig,
  setCurrencyConfig,
  setCurrentOrg,
  setCurrentUser,
  setOrgConfig,
  setOrgFeatureFlags
} from './userSlice';

import theme from '../../../themes/colors';

interface MainHeaderProps {
  orgList: Org[];
  subscriptionData: any;
}

const MainHeader = (props: MainHeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createErrorAlert } = useNotify();
  const { pathname: currentPath } = useLocation();
  const ref = useRef(null);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { currentOrg: currentOrgStored } = useSelector(
    (state: any) => state.user
  );

  const { orgList, subscriptionData } = props;

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const [showUserSettings, setShowUserSettings] = useState<boolean>(false);
  const [accountMethods, setAccountMethods] = useState<any>(null);

  const isTrialMode = useSelector((state: any) => state.trial?.isTrialMode);
  const originalStorageValues = useSelector(
    (state: any) => state.trial?.originalStorageValues
  );
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const hasDemoModeFlag = orgFeatureFlags?.includes(FEATURE_FLAGS.DEMO_MODE);
  const showTrialModeButton = hasDemoModeFlag || isTrialMode;

  const handleTrialModeToggle = () => {
    if (isTrialMode) {
      dispatch(exitTrialMode());
    } else {
      dispatch(enterTrialMode());
    }
  };

  const { currentUser } = useSelector((state: any) => state.user);
  const { activeTourHint } = useSelector((state: any) => state.user);

  const token = getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  const currentOrg = orgList?.find((org: Org) => org.id === organizationId);

  const [getOrgConfig] = useLazyGetOrganizationConfigQuery();
  const [getBranchConfig] = useLazyGetBranchConfigQuery();
  const [getAppConfig] = useLazyAppConfigQuery();
  const [getOrgFeatureFlags] = useLazyGetOrgFeatureFlagsConfigQuery();
  const [updateWelcomeTour, { isLoading: isUpdatingWelcomeTour }] =
    useUpdateWelcomeTourMutation();

  const { currentBranchFilter } = useSelector((state: any) => state.branch);
  const branchId =
    currentBranchFilter?.branchId ||
    getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);
  const branchName =
    currentBranchFilter?.branchName ||
    getLocalStorageItem(STORAGE_KEYS.BRANCH_NAME);

  const isProfileList = PROFILE_ITEM_PATHS.some((path) =>
    currentPath.startsWith(path)
  );

  const isLeadManagementRole = LEAD_MANAGEMENT_ROLES.includes(
    currentOrgStored?.role
  );
  const isEventManagerRole = EVENT_MANAGER_ROLES.includes(
    currentOrgStored?.role
  );

  const showBranchView =
    !isProfileList &&
    (currentOrg?.status === OrgStatus.APPROVED ||
      (currentOrg?.status === OrgStatus.KYC_PENDING &&
        !currentOrg?.is_kyc_deferred)) &&
    currentOrg?.has_plan &&
    currentOrg?.type === AccountType.ORGANIZATION &&
    currentOrgStored?.role &&
    !isLeadManagementRole;

  const [selectedBranch, setSelectedBranch] = useState<string>(branchName);

  const [branchData, setBranchData] = useState<any[]>([]);
  const [getBranches, { data: branchesData }] = useLazyGetAllBranchesQuery();

  // Add a state to track when to force a single refresh
  const [shouldRefreshBranches, setShouldRefreshBranches] =
    useState<boolean>(false);

  // Simplify the getCurrentBranchName function
  const getCurrentBranchName = () => {
    if (branchData?.length && branchId) {
      const currentBranch = branchData.find((branch) => branch.id === branchId);
      if (currentBranch?.name) {
        return currentBranch.name;
      }
    }
    return branchName;
  };

  // Modify the branch data fetching to occur only when needed
  useEffect(() => {
    if (
      organizationId &&
      showBranchView &&
      currentOrg?.type === AccountType.ORGANIZATION
    ) {
      getBranches({ organizationId });
    }
  }, [
    organizationId,
    JSON.stringify(branchesData),
    shouldRefreshBranches,
    currentOrg?.type
  ]);

  const getOrgConfigData = async () => {
    const payload = {
      organizationId: currentOrg?.id || organizationId
    };
    const resp = await getOrgConfig(payload);
    if ('data' in resp) {
      dispatch(setOrgConfig(resp.data));
    }
  };

  const getBranchConfigData = async () => {
    if (!branchId) return;
    if (currentOrg?.type !== AccountType.ORGANIZATION) return;

    const branchConfigResp = await getBranchConfig({
      organizationId: currentOrg?.id || organizationId,
      branchId
    });
    if ('data' in branchConfigResp) {
      dispatch(
        setMemberCustomFields(branchConfigResp.data?.member_custom_fields || [])
      );
    }
  };

  const getOrgFeatureFlagsData = async () => {
    const realOrgId = isTrialMode
      ? originalStorageValues?.[STORAGE_KEYS.ORGANIZATION_ID]
      : null;
    const payload = {
      organizationId: realOrgId || currentOrg?.id || organizationId
    };
    const resp = await getOrgFeatureFlags(payload);
    if ('data' in resp) {
      dispatch(setOrgFeatureFlags(resp.data));
      const flags = resp.data as string[];
      if (isTrialMode && !flags?.includes(FEATURE_FLAGS.DEMO_MODE)) {
        dispatch(exitTrialMode());
      }
    }
  };

  const getAppConfigData = async () => {
    const resp = await getAppConfig(null);
    if ('data' in resp) {
      dispatch(setAppConfig(resp.data));
    }
  };
  const [getBranchById] = useLazyGetBranchByIdQuery();

  const handleGetBranchDetails = async () => {
    const res = await getBranchById({
      params: { organizationId, branchId: branchId || '' },
      queryString: '?include_fee_categories=true&include_payment_stats=false'
    });
    if ('data' in res && res.data?.currency) {
      dispatch(setCurrencyConfig(res.data.currency));
    }
  };

  const { currentOrg: currentStoredOrg } = useSelector(
    (state: any) => state.user
  );

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
    setShowUserSettings(false);
  });

  const handleDropDownClick = () => {
    setShowUserSettings((prev) => !prev);
  };

  const isIndividualLite = (org: Org) =>
    org?.status === OrgStatus.KYC_PENDING &&
    !org?.has_plan &&
    !org?.is_kyc_deferred &&
    org?.type === AccountType.INDIVIDUAL;

  const latestOrg = currentStoredOrg || currentOrg;

  const handleSkipHint = async (userId: string) => {
    const res = await updateWelcomeTour({
      userId
    });
    if ('data' in res) {
      dispatch(setActiveTourHint(null));
      dispatch(
        setCurrentUser({
          user: { ...currentUser, is_welcome_tour_completed: true }
        })
      );
    } else if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(apiError.data?.message);
    }
  };

  const handlePrimaryAction = () => {
    if (activeTourHint === WalkthroughItem.BRANCH_SETTINGS) {
      navigate(BRANCHES.EDIT_BRANCH);
    } else if (activeTourHint === WalkthroughItem.CREATE_GROUP) {
      navigate(GROUPS.ADD_NEW_GROUP);
      handleSkipHint(currentUser?.user_id);
    }
    dispatch(setActiveTourHint(null));
  };

  useEffect(() => {
    if (
      branchId &&
      currentOrgStored?.role &&
      !isLeadManagementRole &&
      !isEventManagerRole
    ) {
      handleGetBranchDetails();
      getBranchConfigData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, organizationId, currentOrgStored?.role]);

  useEffect(() => {
    if (currentOrg?.id === currentStoredOrg?.id) return;
    if (!organizationId) {
      navigate(AUTH.ORG_SELECTION);
      return;
    }
    dispatch(setCurrentOrg(currentOrg));
    dispatch(setAccountType({ type: currentOrg?.type }));
  }, [currentOrg]);

  useEffect(() => {
    if (
      (latestOrg?.status === OrgStatus.APPROVED && !latestOrg.has_plan) ||
      isIndividualLite(latestOrg)
    ) {
      navigate(SUBSCRIPTIONS.NO_PLAN);
    }
  }, [currentPath, currentStoredOrg, currentOrg, navigate]);

  useEffect(() => {
    if (!token) return;
    getAppConfigData();
    getOrgConfigData();
    getOrgFeatureFlagsData();
    if (
      currentOrgStored?.role &&
      !isLeadManagementRole &&
      !isEventManagerRole
    ) {
      getBranchConfigData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoredOrg]);

  useEffect(() => {
    if (branchesData?.data) {
      setBranchData(branchesData.data);
    }
  }, [branchesData]);

  // Update the effect that sets selectedBranch to prioritize quick updates
  useEffect(() => {
    // Immediately update from local storage if available
    const storedBranchName = getLocalStorageItem(STORAGE_KEYS.BRANCH_NAME);
    if (storedBranchName) {
      setSelectedBranch(storedBranchName);
    }

    // Then check for more accurate name from branch data
    const currentName = getCurrentBranchName();
    if (currentName && currentName !== selectedBranch) {
      setSelectedBranch(currentName);
    }
  }, [branchData, branchId, currentBranchFilter]);

  useEffect(() => {
    if (
      currentUser?.user_id &&
      !currentUser?.is_welcome_tour_completed &&
      currentOrg?.type === AccountType.ORGANIZATION &&
      currentOrg?.status === OrgStatus.APPROVED &&
      rolesAndPermissions.permissions?.includes(UserPermissionTypes.BRANCH_EDIT)
    )
      dispatch(setActiveTourHint(WalkthroughItem.BRANCH_SETTINGS));
    else dispatch(setActiveTourHint(null));
  }, [currentUser?.is_welcome_tour_completed, currentOrg?.status]);

  // Add a custom event listener for branch updates
  useEffect(() => {
    const handleBranchUpdate = (e: CustomEvent) => {
      const { branchName: updatedBranchName } = e.detail;

      // Update selected branch immediately
      setSelectedBranch(updatedBranchName);

      // Trigger a single refresh of branch data
      setShouldRefreshBranches((prev) => !prev);
    };

    window.addEventListener(
      'branchUpdate',
      handleBranchUpdate as EventListener
    );
    return () =>
      window.removeEventListener(
        'branchUpdate',
        handleBranchUpdate as EventListener
      );
  }, []);

  return (
    <div className="h-14.5 w-screen border-b border-cofeeNeutral4 bg-white text-content">
      <div className="flex h-14.5 w-full items-center">
        <div className="flex size-full justify-end">
          <div className="flex h-full items-center justify-center gap-2 pr-4">
            {showTrialModeButton && (
              <button
                type="button"
                onClick={handleTrialModeToggle}
                className="flex items-center gap-1.5 rounded-lg bg-cofeeBlue200 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cofeeBlue300"
              >
                {isTrialMode ? 'Back to Organization' : 'Enter Demo Mode'}
              </button>
            )}
            <div
              data-testid={testId.header.profileButton}
              className="relative flex h-fit cursor-pointer items-center justify-center gap-x-2 p-1.5  pr-2.5 hover:rounded-full hover:bg-theme20"
              role="presentation"
              onClick={handleDropDownClick}
              ref={ref}
            >
              <Avatar
                imageId={testId.header.profileButtonIcon}
                name={currentUser?.name || ''}
                className="size-9 min-w-9 rounded-full !border-none text-sm text-cofeeNeutral10"
                bgColor={theme.cofeeGreen1}
              />
              {/* rotate with a transition */}
              <Icon
                name="down"
                stroke={theme.cofeeNeutral8}
                className={
                  showUserSettings
                    ? 'rotate-180 transition-transform duration-300'
                    : 'rotate-0 transition-transform duration-300'
                }
              />
              {showUserSettings && (
                <ProfileSettings
                  orgList={orgList || []}
                  subscriptionData={subscriptionData}
                  accountMethods={accountMethods}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <AccountModals orgList={orgList} setAccountMethods={setAccountMethods} />

      {activeTourHint && (
        <WalkThroughModal
          currentHint={activeTourHint}
          handlePrimaryAction={handlePrimaryAction}
          handleSkip={() => handleSkipHint(currentUser?.user_id)}
          userName={currentUser?.name || ''}
          isLoading={isUpdatingWelcomeTour}
        />
      )}
    </div>
  );
};

export default MainHeader;
